import React, {
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import { Item, useOverlayTriggerState } from "react-stately";
import Tabs from "../../../../../design-system-components/Tabs/Tabs";
import {
    getInitPlaylistAction,
    initCheckedStudentsWithAssignment,
    isPlaylistNameUnique,
    prepareClassroomsForTreeCheckboxGroup,
} from "./utils";
import {
    TreeNode,
    checkOnAllBranches,
    useNodeCheckingStrategy,
} from "../../../../../components/TreeCheckboxGroup";
import SimpleBar from "simplebar-react";
import { useUrl } from "../../../../../hooks/usePage";
import { Pagetype } from "../../../../../interfaces/Config";
import { ResourceSeriesFlowOverrideProvider } from "../../../../../components/Player/ResourceSeriesFlowOverride/ResourceSeriesFlowOverrideContext";
import SendConfirmationModal from "./SendConfirmationModal/SendConfirmationModal";
import { playlistMessages } from "./playlistMessages";
import TeacherPlaylistSend from "./TeacherPlaylistSend/TeacherPlaylistSend";
import TeacherPlaylistDescribe from "./TeacherPlaylistDescribeAndSend/TeacherPlaylistDescribe";
import MobileContainer from "./TeacherPlaylistSend/Components/MobileContainer";
import { sessionStore } from "../../../../../contexts/SessionContext";
import TeacherPlaylistSelect from "./TeacherPlaylistSelect/TeacherPlaylistSelect";
import { CreateSteps, PlaylistStatus } from "../../type";
import { nodePathsToUniqueLeafId } from "../../../../../components/TreeCheckboxGroup/utils";
import { useHistory, useLocation, useParams } from "react-router";
import playlistReducer, { initialState } from "./playlistReducer";
import ActiveResourcePreview from "./ActiveResourcePreview/ActiveResourcePreview";
import {
    EditorBody,
    EditorContainer,
    EditorFooter,
    EditorMainContent,
    EditorPanel,
} from "../Components/EditorLayout/EditorLayout";
import { LegacyLoader } from "../../../../../components/Loader/Loader";
import TeacherPlaylistOrganize from "./TeacherPlaylistOrganize/TeacherPlaylistOrganize";
import {
    useAllPlaylistsQuery,
    usePlaylistAssignmentsQuery,
    usePlaylistQuery,
} from "../../api/useQueries";
import {
    useCreateAssignmentMutation,
    useCreatePlaylistMutation,
    useUpdateAssignmentMutation,
    useUpdatePlaylistMutation,
} from "../../api/useMutations";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import Player from "../../../../../components/Player/Player";
import SingleResourceSeriesProvider from "./SingleResourceSeriesProvider";
import { CreateURLParams } from "../const";
import useResourceURL from "./useResourceURL";
import {
    getActivityById,
    getObjectiveById,
} from "../../../../../utils/dataRetrieval";
import { Data } from "../../../../../interfaces/Data";
import TabTitle from "../../../../../components/TabTitle/TabTitle";
import { pageTitles } from "../../../../../utils/messages";
import { useGroups } from "../../../../DashBoard/DashboardTeacher/Group/api/useQueries";
import usePlaylistableResources from "./usePlaylistableResources";
import { useAllSubjectsData, useData } from "../../../../../hooks/useData";
import { ModuleResourcePath, ResourcePath } from "../utils";
import { isLoading } from "../../../../../hooks/queries/utils";
import { usePlaylistableCollections } from "../../api/usePlaylistableCollections";
import useOnlineStatus from "../../../../../hooks/useOnlineStatus";
import { withExercises } from "../../../../../hooks/queries/useExercisesQuery";

import "./EditPlaylist.scss";

const EditPlaylist = () => {
    const intl = useIntl();
    const isOnline = useOnlineStatus();
    const data = useAllSubjectsData();
    const { data: collections, queries: collectionQueries } =
        usePlaylistableCollections();
    const {
        session: { userId, classrooms },
    } = useContext(sessionStore);
    const history = useHistory();
    const playlistModeUrl = useUrl(Pagetype.WORKMODE_PLAYLIST);
    const { isTabletOrSmaller } = useBreakpoints();
    const { playlistId: existingPlaylistId } = useParams<{
        playlistId?: string;
    }>();

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const isEditDraftPlaylistAssignement = queryParams.get(
        "isEditDraftPlaylistAssignement"
    );

    const [step, setStep] = useState(
        isEditDraftPlaylistAssignement
            ? CreateSteps.describeAndSend
            : CreateSteps.select
    );

    const sendModalState = useOverlayTriggerState({});

    const { resources, entryIds } = usePlaylistableResources();

    const groupsQuery = useGroups();
    const groups = groupsQuery.data;
    const students = useMemo<Record<string, TreeNode>>(
        () =>
            prepareClassroomsForTreeCheckboxGroup([
                ...(classrooms ?? []),
                ...(groups ?? []),
            ]),
        [classrooms, groups]
    );

    const allPlaylistsResult = useAllPlaylistsQuery();
    const existingPlaylistResult = usePlaylistQuery(existingPlaylistId ?? "", {
        enabled: !!existingPlaylistId,
    });
    const existingAssignmentResult = usePlaylistAssignmentsQuery(
        existingPlaylistId ?? "",
        {
            enabled: !!existingPlaylistId,
        }
    );

    const [playlist, dispatch] = useReducer(
        playlistReducer,
        initialState(userId)
    );
    const initializedPlaylist = useRef(false);
    useEffect(() => {
        if (
            initializedPlaylist.current ||
            allPlaylistsResult.isLoading ||
            (existingPlaylistId && existingPlaylistResult.isLoading) ||
            collectionQueries.some(isLoading)
        )
            return;
        try {
            const initAction = getInitPlaylistAction(
                existingPlaylistId,
                existingPlaylistResult,
                allPlaylistsResult,
                { data, collections },
                intl
            );
            dispatch(initAction);
            initializedPlaylist.current = true;
        } catch {
            // TODO: Error handling
            history.push(playlistModeUrl);
        }
    }, [
        allPlaylistsResult,
        existingPlaylistId,
        existingPlaylistResult,
        intl,
        history,
        playlistModeUrl,
        data,
        collections,
        collectionQueries,
    ]);

    const {
        checkedLeaves: checkedStudents,
        toggleNode: toggleStudents,
        dirty: checkedStudentsIsDirty,
        setCheckedLeaves: setCheckedStudents,
    } = useNodeCheckingStrategy(students ?? {}, checkOnAllBranches);
    const [initializedAssignment, setInitializedAssignment] = useState<boolean>(
        existingPlaylistId ? false : true
    );
    useEffect(() => {
        if (initializedAssignment || !existingAssignmentResult.data) return;
        if (existingAssignmentResult.data.length === 0) {
            setInitializedAssignment(true);
            return;
        }
        if (existingAssignmentResult.data.length > 1)
            throw new Error("Malformed assignment payload");

        const checkedStudents = initCheckedStudentsWithAssignment(
            existingAssignmentResult.data[0],
            classrooms
        );
        setCheckedStudents(checkedStudents, false);
        setInitializedAssignment(true);
    }, [
        existingAssignmentResult.data,
        setCheckedStudents,
        students,
        classrooms,
        initializedAssignment,
    ]);

    const {
        mutateAsync: mutateExistingPlaylist,
        isLoading: isMutateExistingPlaylistLoading,
    } = useUpdatePlaylistMutation();
    const {
        mutateAsync: mutateNewPlaylist,
        isLoading: isMutateNewPlaylistLoading,
    } = useCreatePlaylistMutation();
    const {
        mutateAsync: mutateExistingAssignment,
        isLoading: isMutateExistingAssignmentLoading,
    } = useUpdateAssignmentMutation();
    const {
        mutateAsync: mutateNewAssignment,
        isLoading: isMutateNewAssignementLoading,
    } = useCreateAssignmentMutation();

    if (!playlist) return <LegacyLoader />;

    const hasExistingPlaylist = !!playlist?.learning_set_id;
    const hasExistingAssignment =
        existingAssignmentResult.data &&
        existingAssignmentResult.data.length === 1;
    const canSavePlaylist =
        playlist.name &&
        isPlaylistNameUnique(allPlaylistsResult.data, playlist);
    const canSaveAssignment =
        hasExistingAssignment || checkedStudents.length > 0;

    const canSaveDraft =
        (canSavePlaylist && playlist.dirty) ||
        (hasExistingPlaylist && canSaveAssignment && checkedStudentsIsDirty);
    const canSendPlaylist =
        playlist.learning_items.length > 0 && checkedStudents.length > 0;

    const saveAssignment = (playlistId: string) => {
        if (!canSaveAssignment) return;
        if (hasExistingAssignment)
            return mutateExistingAssignment({
                id: existingAssignmentResult.data![0].id,
                students: nodePathsToUniqueLeafId(checkedStudents, students),
                teacher: userId,
                assigned_resource: {
                    resource_id: playlistId,
                    resource_type: "playlist",
                },
            });
        else
            return mutateNewAssignment({
                students: nodePathsToUniqueLeafId(checkedStudents, students),
                teacher: userId,
                assigned_resource: {
                    resource_id: playlistId,
                },
            });
    };

    const saveHandler = (status: PlaylistStatus) => async () => {
        // Assignment can only be saved if the playlist exists, so the strategy
        // should be :
        // - save playlist and save/create assignment in parallel if playlist
        //   already exists
        // - create playlist and then create assignment if the playlist doesn't
        //   already exist

        if (hasExistingPlaylist) {
            const promises: (Promise<any> | undefined)[] = [];
            if (playlist.dirty || status !== playlist.status.status)
                promises.push(
                    mutateExistingPlaylist({ ...playlist, status: { status } })
                );
            if (checkedStudentsIsDirty)
                promises.push(saveAssignment(playlist.learning_set_id!));
            try {
                await Promise.all(promises);
            } catch (err) {
                // TODO handle error
                return;
            }
        } else {
            try {
                const data = await mutateNewPlaylist({
                    ...playlist,
                    status: { status },
                });
                if (checkedStudentsIsDirty && data) {
                    try {
                        await saveAssignment(data.learning_set_id);
                    } catch {
                        // TODO: handle error
                        return;
                    }
                }
            } catch (err) {
                // TODO: Handle error
                return;
            }
        }
        history.push(`${playlistModeUrl}`);
    };
    const isSaving =
        isMutateExistingAssignmentLoading ||
        isMutateNewAssignementLoading ||
        isMutateExistingPlaylistLoading ||
        isMutateNewPlaylistLoading;

    return (
        <>
            <TabTitle
                title={
                    hasExistingPlaylist
                        ? intl.formatMessage(pageTitles.teacherPlaylistEdit, {
                              playlist: playlist.name,
                          })
                        : pageTitles.teacherPlaylistCreate
                }
            />

            <EditorContainer className="edit-playlist">
                <EditorBody>
                    <EditorPanel
                        title={{
                            content: intl.formatMessage(
                                messages.playlistEditorTitle
                            ),
                        }}
                        backTo={playlistModeUrl}
                    >
                        <Tabs
                            className="edit-playlist__panel-tabs"
                            variant="accent"
                            onSelectionChange={(key) =>
                                setStep(key as CreateSteps)
                            }
                            selectedKey={step}
                        >
                            <Item
                                title={intl.formatMessage(
                                    messages.playlistCreateSelect
                                )}
                                key={CreateSteps.select}
                            >
                                <SimpleBar className="edit-playlist__tab-content-container">
                                    <TeacherPlaylistSelect
                                        checkedResources={
                                            playlist.learning_items
                                        }
                                        resources={resources}
                                        entryIds={entryIds}
                                        dispatch={dispatch}
                                    />
                                </SimpleBar>
                            </Item>

                            <Item
                                title={intl.formatMessage(
                                    messages.playlistCreateOrganise,
                                    {
                                        resourcesCount:
                                            playlist.learning_items.length,
                                    }
                                )}
                                key={CreateSteps.organize}
                            >
                                <TeacherPlaylistOrganize
                                    resources={playlist.learning_items}
                                    dispatch={dispatch}
                                />
                            </Item>

                            <Item
                                title={intl.formatMessage(
                                    playlistMessages.recipients
                                )}
                                key={CreateSteps.describeAndSend}
                            >
                                {initializedAssignment ? (
                                    <SimpleBar className="edit-playlist__tab-content-container">
                                        <div className="edit-playlist__tab-content-wrapper">
                                            <TeacherPlaylistSend
                                                students={students}
                                                checkedStudents={
                                                    checkedStudents
                                                }
                                                toggleStudents={toggleStudents}
                                            />

                                            {isTabletOrSmaller && (
                                                <MobileContainer
                                                    label={intl.formatMessage(
                                                        playlistMessages.playlistDescribeMobileLabel
                                                    )}
                                                    className="edit-playlist__mobile-describe"
                                                >
                                                    <TeacherPlaylistDescribe
                                                        dispatch={dispatch}
                                                        playlist={playlist}
                                                    />
                                                </MobileContainer>
                                            )}
                                        </div>
                                    </SimpleBar>
                                ) : (
                                    <LegacyLoader />
                                )}
                            </Item>
                        </Tabs>
                    </EditorPanel>

                    {(() => {
                        switch (step) {
                            case CreateSteps.select:
                            case CreateSteps.organize: {
                                return (
                                    <ActiveResourcePreview
                                        resources={resources}
                                    >
                                        <ResourceSeriesFlowOverrideProvider>
                                            <SingleResourceSeriesProvider
                                                step={step}
                                            >
                                                <Player
                                                    opts={{ skipIntro: true }}
                                                />
                                            </SingleResourceSeriesProvider>
                                        </ResourceSeriesFlowOverrideProvider>
                                    </ActiveResourcePreview>
                                );
                            }

                            case CreateSteps.describeAndSend: {
                                return (
                                    <EditorMainContent>
                                        <TeacherPlaylistDescribe
                                            dispatch={dispatch}
                                            playlist={playlist}
                                        />
                                    </EditorMainContent>
                                );
                            }
                        }
                    })()}
                </EditorBody>

                <EditorFooter
                    button1={{
                        onPress: saveHandler(PlaylistStatus.DRAFT),
                        label: intl.formatMessage(messages.playlistSaveButton),
                        disabled: !canSaveDraft || !isOnline || isSaving,
                    }}
                    button2={{
                        label: intl.formatMessage(
                            messages.playlistValidateButton
                        ),
                        onPress: () => sendModalState.open(),
                        disabled: !canSendPlaylist || !isOnline,
                        loading: isSaving,
                    }}
                />
            </EditorContainer>

            <SendConfirmationModal
                state={sendModalState}
                playlist={playlist}
                resources={playlist.learning_items}
                students={checkedStudents}
                onConfirm={saveHandler(PlaylistStatus.SENT)}
                isConfirmSaving={isSaving}
            />
        </>
    );
};

const withActiveItem =
    <T extends React.JSX.IntrinsicAttributes>(
        Component: (props: T) => React.JSX.Element
    ): ((props: T) => React.JSX.Element) =>
    (props: T) => {
        const data = useData();
        const urlParams = useParams<CreateURLParams>();
        const { goToResourceURL } = useResourceURL();
        if (!urlParams.resourceType) {
            const firstItemPath = getFirstItemPath(data);
            if (firstItemPath) {
                goToResourceURL(firstItemPath.toEditorPath());
                return <LegacyLoader />;
            }
        }
        return <Component {...props} />;
    };

export default withActiveItem(withExercises(EditPlaylist));

const getFirstItemPath = (data: Data): ResourcePath | undefined => {
    const mod = data.modules[0];
    if (!mod) return undefined;
    const obj = getObjectiveById(mod.objectiveIds[0], data);
    const act = getActivityById(obj.activityIds[0], data);
    return ModuleResourcePath.fromMOAE({
        exerciseId: act.exerciseIds[0],
        activityId: act.id,
        objectiveId: obj.id,
        moduleId: mod.id,
    });
};

const messages = defineMessages({
    playlistCreateSelect: {
        id: "playlist-create-select",
        defaultMessage: "Select",
    },
    playlistCreateOrganise: {
        id: "playlist-organize",
        defaultMessage: "Organize ({resourcesCount})",
    },
    playlistNew: {
        id: "playlist-new",
        defaultMessage: "New playlist",
    },
    playlistSaveButton: {
        id: "playlist-save",
        defaultMessage: "Save",
    },
    playlistValidateButton: {
        id: "playlist-validate",
        defaultMessage: "Send to selected ",
    },
    playlistEditorTitle: {
        id: "playlist-editor-title",
        defaultMessage: "Playlist",
    },
});
